import React, { useContext, useEffect, useState } from "react";
import { CurrentUserContext } from "App";
import LikeNotification from "../../components/notifications/likes/LikeNotification";
import FollowingNotification from "../../components/notifications/following/FollowingNotification";
import ConnectWeekComponent from "../../components/notifications/connectWeek/ConnectWeekComponent";
import defaultAvatar from "assets/sampleAvatar.png";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3080";

function timeAgo(dateString) {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function groupByMonth(notifications) {
  const groups = {};
  notifications.forEach((n) => {
    const date = new Date(n.created_at);
    const key = date.toLocaleString("default", { month: "long", year: "numeric" });
    if (!groups[key]) groups[key] = [];
    groups[key].push(n);
  });
  return groups;
}

export default function NotificationsPage() {
  const { currentUser } = useContext(CurrentUserContext);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser?.token) return;

    fetch(`${API_URL}/getNotifications`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentUser.token}`,
      },
      body: JSON.stringify({ limit: 50, offset: 0 }),
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.message === "success" && r.notifications) {
          setNotifications(r.notifications);
          // Mark unread notifications as read
          const unreadIds = r.notifications
            .filter((n) => n.is_unread)
            .map((n) => n.notification_id);
          if (unreadIds.length > 0) {
            fetch(`${API_URL}/markNotificationsRead`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${currentUser.token}`,
              },
              body: JSON.stringify({ notification_ids: unreadIds }),
            }).catch((err) => console.error("Error marking read:", err));
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching notifications:", err);
        setLoading(false);
      });
  }, [currentUser?.token]);

  const handleFollowBack = (username, currentStatus) => {
    const newStatus = currentStatus === "followed" ? "unfollowed" : "followed";
    fetch(`${API_URL}/updateUserConnections`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentUser.token}`,
      },
      body: JSON.stringify({ username, status: newStatus }),
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.message === "success") {
          setNotifications((prev) =>
            prev.map((n) =>
              n.sender_username === username
                ? {
                    ...n,
                    connection_status: newStatus === "followed" ? "followed" : "not_connected",
                  }
                : n
            )
          );
        }
      })
      .catch((err) => console.error("Error updating connection:", err));
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ paddingTop: "1rem" }}>
        <div className="spinner-border text-light" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Group all notifications by month for the unified view
  const grouped = groupByMonth(notifications);

  return (
    <div style={{ paddingTop: "1rem", paddingBottom: "2rem" }}>
      {/* Header — only visible on desktop (MobileHeader handles mobile) */}
      <div className="d-none d-md-flex align-items-center gap-3 mb-4 px-3">
        <button
          className="btn p-0"
          onClick={() => window.history.back()}
          style={{ color: "var(--main-purple)", fontSize: "1.5rem" }}
        >
          <ion-icon name="arrow-back-outline"></ion-icon>
        </button>
        <h2 className="fw-bold m-0" style={{ color: "white", fontSize: "1.8rem" }}>
          Notifications
        </h2>
      </div>

      {notifications.length === 0 ? (
        <div className="text-center mt-5" style={{ color: "rgba(185, 151, 255, 0.5)" }}>
          <p style={{ fontSize: "1.1rem" }}>No notifications yet</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px", padding: "0 12px" }}>
          {Object.entries(grouped).map(([month, items]) => (
            <div key={month}>
              <h6
                style={{
                  fontWeight: 700,
                  textTransform: "uppercase",
                  marginBottom: "10px",
                  marginTop: "4px",
                  color: "rgba(185, 151, 255, 0.6)",
                  fontSize: "0.75rem",
                  letterSpacing: "0.1em",
                }}
              >
                {month}
              </h6>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {items.map((notif) => {
                  const avatar =
                    notif.sender_avatar && notif.sender_avatar !== "sampleAvatar.png"
                      ? notif.sender_avatar
                      : defaultAvatar;
                  const time = timeAgo(notif.created_at);

                  if (notif.notification_type === "like") {
                    return (
                      <LikeNotification
                        key={notif.notification_id}
                        senderUsername={notif.sender_username}
                        senderAvatar={avatar}
                        dailyUpdateTitle={notif.daily_update_title || "your memory"}
                        timeAgo={time}
                        isUnread={notif.is_unread}
                      />
                    );
                  }

                  if (notif.notification_type === "follow") {
                    return (
                      <FollowingNotification
                        key={notif.notification_id}
                        senderUsername={notif.sender_username}
                        senderAvatar={avatar}
                        timeAgo={time}
                        isUnread={notif.is_unread}
                        connectionStatus={notif.connection_status}
                        onFollowBack={() =>
                          handleFollowBack(notif.sender_username, notif.connection_status)
                        }
                      />
                    );
                  }

                  if (notif.notification_type === "invite") {
                    return (
                      <ConnectWeekComponent
                        key={notif.notification_id}
                        senderUsername={notif.sender_username}
                        senderAvatar={avatar}
                        timeAgo={time}
                        isUnread={notif.is_unread}
                      />
                    );
                  }

                  return null;
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
